import { Button, Paper, Stack, Text } from "@mantine/core";
import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { ReactNode, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { isUnreachable } from "../../../helpers/errors.helpers";
import useServerStore from "../../../store/server";
import { LinkButton } from "../../LinkButton/LinkButton";
import {
  routeOutcomeLog,
  routeRecoveryBudget,
} from "../../ServerRecovery/recoveryBudget";
import SkillsterTitle from "../../SkillsterTitle/SkillsterTitle";

function ErrorComponent({
  title,
  description,
  bottomComponent,
  error,
}: {
  title?: string;
  description?: string;
  bottomComponent?: ReactNode;
  error?: unknown;
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const { reset } = useQueryErrorResetBoundary();
  const unreachable = isUnreachable(error);
  const flagUp = useServerStore((state) => state.unreachable);
  const sawFlag = useRef(false);

  // Lets a useSuspenseQuery that errored fetch again when the route re-renders.
  useEffect(() => {
    reset();
  }, [reset]);

  // Records that the error page showed for this path, which cancels any
  // earlier success evidence. `error` so a new error counts even if React keeps
  // this instance.
  useEffect(() => {
    routeOutcomeLog.errorShown(router.state.location.pathname, Date.now());
  }, [router, error]);

  // Reload by itself once the recovery poll (ServerRecovery) has cleared the flag. The
  // ref means an error that never raised the flag cannot loop into invalidates.
  useEffect(() => {
    if (!unreachable) return;
    if (flagUp) {
      sawFlag.current = true;
    } else if (sawFlag.current) {
      sawFlag.current = false;
      // The budget for a path resets after the path resolved without an error
      // and no error page was shown for it since (see routeOutcomeLog); the
      // manual button always works.
      const pathname = router.state.location.pathname;
      const allowed = routeRecoveryBudget.select(
        [
          {
            queryHash: pathname,
            dataUpdatedAt: routeOutcomeLog.lastSuccessAt(pathname),
          },
        ],
        Date.now(),
      );
      if (allowed.length > 0) router.invalidate();
    }
  }, [unreachable, flagUp, router]);

  const shownTitle = unreachable
    ? t("common.serverUnreachable.title")
    : (title ?? t("common.feedback.error"));
  const shownDescription = unreachable
    ? t("common.queryError.unreachable.description")
    : description;
  const shownBottom = unreachable ? (
    <Button onClick={() => router.invalidate()}>
      {t("common.queryError.retry")}
    </Button>
  ) : (
    bottomComponent || (
      <LinkButton to="/home">{t("common.pageNotFound.button")}</LinkButton>
    )
  );

  return (
    <Paper bg="bluegray.1" py={200}>
      <Stack align="center">
        <SkillsterTitle order={2}>{shownTitle}</SkillsterTitle>
        {shownDescription && <Text c="dimmed">{shownDescription}</Text>}
        {shownBottom}
      </Stack>
    </Paper>
  );
}

export default ErrorComponent;
